/**
 * Campaign notification preference helpers.
 *
 * `isTeamMember` = invite / access (especially for workspace guests).
 * `receivesNotifications` = whether the user gets campaign emails / in-app notifs.
 * The campaigns-list bell toggles only `receivesNotifications`.
 */

/** Whether the current user should show as receiving notifications (list bell, emails). */
export const resolveReceivesCampaignNotifications = ({
  isCampaignOwner,
  campaignUserDetails,
}: {
  isCampaignOwner: boolean;
  campaignUserDetails?: { receivesNotifications: boolean } | null;
}): boolean =>
  campaignUserDetails?.receivesNotifications === true ||
  (isCampaignOwner &&
    (campaignUserDetails === null || campaignUserDetails === undefined));

/**
 * Default notification preference when creating campaign_user_details.
 * Invite creates with isTeamMember true → notifications on unless overridden.
 * Star creates with isTeamMember false → notifications off unless overridden.
 */
export const defaultReceivesNotificationsOnCreate = ({
  isTeamMember,
  receivesNotifications,
}: {
  isTeamMember: boolean;
  receivesNotifications?: boolean;
}): boolean => receivesNotifications ?? isTeamMember;

/**
 * Value to write when the user has no campaign_user_details row yet and clicks the bell.
 * Owners are treated as already opted in, so first click opts out.
 * Everyone else is opting in.
 */
export const initialReceivesNotificationsOnBellCreate = (
  isCampaignOwner: boolean
): boolean => !isCampaignOwner;

/** Flip the notification preference for an existing campaign_user_details row. */
export const nextReceivesNotificationsOnBellToggle = (
  currentReceivesNotifications: boolean
): boolean => !currentReceivesNotifications;
